import { type RefObject, useEffect } from 'react';

import { defineMessages, useIntl } from 'react-intl-next';

import type { FlagsContainer } from '../context/FlagsProviderContext';
import { getCustomization, setIsPaused, setIsPlaying } from '../state/AudioPlayerState';


const i18n = defineMessages({
	errorLoadingAudioPreferences: {
		id: 'ai-audio.audio-container.error-loading-audio-preferences',
		defaultMessage: 'Playback speed error',
		description: 'Error message shown when audio preferences fail to load',
	},
	errorDescription: {
		id: 'ai-audio.audio-container.error-loading-audio-preferences-description',
		defaultMessage: 'Please try again later',
		description: 'Error description shown when audio preferences fail to load',
	},
});

import { useAudioPlayerStore } from './useAudioPlayerStore';

type UseAudioPlaybackSyncProps = {
	audioRef: RefObject<HTMLAudioElement>;
	onBuffer?: () => void;
	flags: FlagsContainer;
};

export const useAudioPlaybackSync = ({ flags, audioRef, onBuffer }: UseAudioPlaybackSyncProps): void => {
	const { formatMessage } = useIntl();
	const { isPaused } = useAudioPlayerStore();

	useEffect(() => {
		const audio = audioRef.current;
		if (!audio) {
			return;
		}

		const onPlay = () => {
			setIsPlaying(true);
			setIsPaused(false);
		};

		const onPause = () => {
			setIsPlaying(false);

			if (!audio.src || audio.src === '') {
				return;
			}

			setIsPaused(true);
		};

		const onEnded = () => {
			setIsPlaying(false);
			setIsPaused(false);
		};

		const onCanPlay = () => {
			if (audio.currentTime < audio.duration) {
				const customization = getCustomization();
				if (customization.playbackSpeed && customization.playbackSpeed !== audio.playbackRate) {
					try {
						audio.playbackRate = customization.playbackSpeed;
					} catch {
						flags?.showFlag({
							appearance: 'error',
							title: formatMessage(i18n.errorLoadingAudioPreferences),
							description: formatMessage(i18n.errorDescription),
							close: 'auto',
						});
					}
				}
				audio.play();
			}
		};

		const handleBuffer = () => {
			onBuffer?.();
		};

		// Attach event listeners
		audio.addEventListener('play', onPlay);
		audio.addEventListener('pause', onPause);
		audio.addEventListener('canplay', onCanPlay);
		audio.addEventListener('ended', onEnded);
		audio.addEventListener('waiting', handleBuffer);

		return () => {
			// Cleanup event listeners
			audio.removeEventListener('play', onPlay);
			audio.removeEventListener('pause', onPause);
			audio.removeEventListener('canplay', onCanPlay);
			audio.removeEventListener('ended', onEnded);
			audio.removeEventListener('waiting', handleBuffer);
		};
	}, [audioRef, onBuffer, flags, formatMessage]);

	// If state to pause is set programmatically, pause the audio
	useEffect(() => {
		if (isPaused && audioRef.current?.src && audioRef.current?.src !== '' && !audioRef.current?.paused) {
			audioRef.current?.pause();
		}
	}, [isPaused, audioRef]);
};
